import { useSpinnerContext, useSessionContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import Credentials from "./credentials";
import { useNavigate } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { PostHogEvents, capturePosthogEvent } from '@analytics';
import { useFeatureFlag, useFormSubmission } from '@hooks';
import { FeatureFlag } from '@root/enums';
import { startOAuthLogin, takeOAuthLoginResult, type OAuthError, type OAuthProvider } from '@stores';
import { Button } from '@common';
import { SSO_PROVIDERS, getSsoProviderName } from '@shared';
import { X } from 'lucide-react';

interface SetupCredentialsProps {
    initial: {
        email: string;
    };
    success: () => void;
}

const getUnknownPayorMessage = (providerName: string) => (
    <>
        Unable to sign in with {providerName}. Please contact us at{` `}
        <a className="underline" href="mailto:help@sunbound.ai">help@sunbound.ai</a>
        {` `}for assistance.
    </>
);
const getPayorMismatchMessage = (providerName: string, email: string) => (
    <>
        This {providerName} account doesn{`'`}t match the email for this signup link. Please continue with{` `}
        <span className="font-medium">{email}</span>
        {` `}or create a password instead.
    </>
);

const getOAuthErrorMessage = (error: OAuthError, provider: OAuthProvider, email: string): ReactNode => {
    const providerName = getSsoProviderName(provider);
    if (error === `payor_mismatch`) return getPayorMismatchMessage(providerName, email);
    if (error === `unknown_payor`) return getUnknownPayorMessage(providerName);
    return `We couldn't sign you in with ${providerName}. Please try again.`;
};

const SetupCredentials = ({ initial, success }: SetupCredentialsProps) => {

    const { setSpinnerShow } = useSpinnerContext();
    const { rawSession, setSession } = useSessionContext();
    const navigate = useNavigate();
    const { value: isSsoEnabled } = useFeatureFlag(FeatureFlag.SSO_ENABLED);
    const {
        setFormState: setSsoLoginError,
        clearFormState: clearSsoLoginError,
        hasFormError: hasSsoLoginError,
        formError: ssoLoginError
    } = useFormSubmission<ReactNode>();

    const { email } = initial;

    useEffect(() => {
        const oauthLoginResult = takeOAuthLoginResult();
        if (!oauthLoginResult) return;

        if (oauthLoginResult.status === `error`) {
            setSsoLoginError(getOAuthErrorMessage(oauthLoginResult.error, oauthLoginResult.provider, email));
            return;
        }

        capturePosthogEvent(PostHogEvents.ACCOUNT_CREATED, { email });
        success();
    }, [email, success, setSsoLoginError]);

    const handleSsoLogin = (provider: OAuthProvider) => {
        clearSsoLoginError();
        startOAuthLogin(provider, window.location.pathname);
    };

    const handleSubmit = async (values: { password: string }) => {

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.updatePassword(values.password);
            if (rawSession) {
                setSession({ ...rawSession, hasPassword: true });
            }
            capturePosthogEvent(PostHogEvents.ACCOUNT_CREATED, { email });
            success();
        } catch (error) {
            // setFormState(`Unable to process your request, please contact ${Constants.Help.Email} for support`);
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const handleCancel = () => {
        navigate(`/login`);
    };

    return (
        <div className="flex flex-col w-full gap-6">
            <Credentials submit={handleSubmit} initial={{ email }} onCancel={handleCancel}>
                <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">Create Your Account</h1>
            </Credentials>
            {isSsoEnabled && (
                <>
                    <div className="flex items-center gap-4">
                        <div className="flex-1 border-t border-neutral-200" />
                        <span className="text-sm text-neutral-500">or</span>
                        <div className="flex-1 border-t border-neutral-200" />
                    </div>
                    {SSO_PROVIDERS.map(({ provider, name, logo: Logo }) => (
                        <Button
                            key={provider}
                            variant="secondary"
                            size="lg"
                            className="w-full"
                            startIcon={<Logo className="mr-2" />}
                            onClick={() => handleSsoLogin(provider)}
                        >
                            Continue with {name}
                        </Button>
                    ))}
                    {
                        hasSsoLoginError &&
                        <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                            <p className="flex-1">{ssoLoginError}</p>
                            <button
                                type="button"
                                aria-label="Dismiss sign-in error"
                                className="shrink-0 text-red-700 hover:text-red-900"
                                onClick={clearSsoLoginError}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    }
                </>
            )}
        </div>
    );

};

export default SetupCredentials;
